import React from 'react';
import styled from 'styled-components';

interface IProps extends React.InputHTMLAttributes<HTMLButtonElement> {
    children: React.ReactElement | string;
    onClick: (...props: any) => void;
    height?: string;
    margin?: string;
    width?: string;
    padding?: string;
    color?: 'primary';
    flexed?: boolean;
    variant?: TButtonVariants;
    disabled?: boolean;
    noBorderRadius?: boolean;
    textTransform?: 'uppercase' | 'lowercase' | 'capitalize';
    flexGrow?: boolean;
}

const Button = ({
    children,
    color,
    disabled,
    flexGrow,
    flexed,
    height,
    margin,
    noBorderRadius,
    onClick,
    padding,
    textTransform = 'uppercase',
    variant,
    width,
}: IProps): JSX.Element => (
    <ButtonContainer
        flexGrow={flexGrow}
        margin={margin}
        disabled={disabled}
        onClick={onClick}
        padding={padding}
        variant={variant}
        color={color}
        noBorderRadius={noBorderRadius}
        width={width}>
        <ContentWrapper height={height} flexed={flexed} textTransform={textTransform}>
            {children}
        </ContentWrapper>
    </ButtonContainer>
);

type TButtonVariants = 'primary' | 'secondary' | 'warning' | 'text' | null;

type TButtonProps = {
    margin?: string;
    padding?: string;
    variant?: TButtonVariants;
    width?: string;
    noBorderRadius?: boolean;
    color?: 'primary'; // ... more
    disabled?: boolean;
    flexGrow?: boolean;
};

const ButtonContainer = styled.button<TButtonProps>`
    background-color: ${({ theme }): string => theme.colors.brandSecondary};
    color: white;
    transition: all ease-in 0.25s;
    ${({ noBorderRadius }): string => (noBorderRadius ? `border-radius: 0` : `border-radius: 3px`)};
    box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.11);

    ${({ margin }): string => (margin ? `margin: ${margin};` : '')}
    ${({ padding }): string => (padding ? `padding: ${padding};` : 'padding: 3px 14px;')}
    ${({ width }): string => (width ? `width: ${width};` : '')}
    ${({ color, theme, variant }): string => {
        let fontColor: string | null = null;
        if (color === 'primary') {
            fontColor = theme.colors.brandSecondary;
        }

        switch (variant) {
            case 'primary':
                return `
                    background-color: ${theme.colors.brandSecondary};
                    color: ${fontColor || theme.colors.bgPrimary};
                    &:disabled {
                       background-color: ${theme.colors.bodyLight};
                    }
                    `;
            case 'secondary':
                return `
                    color: ${fontColor || theme.colors.body};
                    background-color: ${theme.colors.bgPrimary};
                    border: solid 1px ${theme.colors.bodyLight};
                    &:disabled {
                        background-color: ${theme.colors.bodyLight}};
                    }
                    `;
            case 'warning':
                return `
                    background-color: ${theme.colors.generalWarning};
                    border: solid 1px ${theme.colors.bodyLight};
                    &:disabled {
                        background-color: ${theme.colors.bodyLight}};
                    }
                    `;
            case 'text':
                return `
                    color: ${fontColor || theme.colors.body};
                    background: none;
                    border: none;
                    box-shadow: none;
                    &:hover {
                        color: ${theme.colors.brandSecondary};
                    }
                    &:disabled {
                        color: ${theme.colors.bodyMid}
                    }
                    `;
            default:
                return '';
        }
    }}
    ${({ flexGrow }): string => (flexGrow ? `flex-grow: 1;` : '')}
`;

type TContentWrapper = {
    height?: string;
    flexed?: boolean;
    textTransform: 'uppercase' | 'lowercase' | 'capitalize';
};

const ContentWrapper = styled.span<TContentWrapper>`
    font-size: 1.08333rem;
    font-weight: bold;
    text-transform: ${({ textTransform }): string => textTransform};
    text-align: center;
    padding: 10px;
    transition: opacity 0.25s ease-in-out 0s;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    ${({ height }): string => (height ? `height: ${height}` : '')};
    ${({ flexed }): string => (flexed ? 'display: flex' : '')};
`;

export default Button;
